import { useState, useCallback, useRef } from 'react';
import {
  canSwitchCamera,
  detectInAppBrowserByUserAgent,
  FacingMode,
  createVideoConstraints,
  RecordingQuality,
  isAndroid,
} from '@common/media';

interface UseMediaTestProps {
  onTestComplete?: () => void;
}

const CAMERA_COMPATIBILITY_WARNING =
  'Camera may not work in this browser. This could be due to an in-app browser or limited camera access.';

export function useMediaTest({ onTestComplete }: UseMediaTestProps = {}) {
  const [showMediaTestModal, setShowMediaTestModal] = useState(false);
  const [testPermissions, setTestPermissions] = useState<{
    video?: boolean;
    audio?: boolean;
  }>({});
  const [isFrontCamera, setIsFrontCamera] = useState(true);
  const [isStreamLoading, setIsStreamLoading] = useState(false);
  const [isSwitchingCamera, setIsSwitchingCamera] = useState(false);
  const [hasStartedTest, setHasStartedTest] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [browserWarning, setBrowserWarning] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState<number>(0);

  // Refs for audio analysis
  const audioStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number>();
  const hasAudioTestDetected = useRef(false);

  // Open media test dialog for specific permissions
  const openMediaTest = (permissions: { video?: boolean; audio?: boolean }) => {
    setTestPermissions(permissions);
    setShowMediaTestModal(true);
  };

  // Open camera test only
  const openCameraTest = () => {
    openMediaTest({ video: true });
  };

  // Open microphone test only
  const openMicrophoneTest = () => {
    openMediaTest({ audio: true });
  };

  // Open both camera and microphone test
  const openBothMediaTest = () => {
    openMediaTest({ video: true, audio: true });
  };

  // Close media test dialog
  const closeMediaTest = () => {
    setShowMediaTestModal(false);
    setTestPermissions({});
  };

  // Handle test completion and continue
  const handleTestComplete = () => {
    closeMediaTest();
    onTestComplete?.();
  };

  // Browser compatibility check (in-app browser detection only)
  const checkBrowserCompatibility = useCallback(
    (permissions?: { video?: boolean; audio?: boolean }) => {
      const effectivePermissions = permissions || testPermissions;

      if (effectivePermissions.video) {
        // Only check for in-app browser issues, not camera functionality
        // Camera testing is handled by react-webcam onUserMedia/onUserMediaError
        const isInAppBrowser = detectInAppBrowserByUserAgent();

        if (isInAppBrowser) {
          setBrowserWarning(CAMERA_COMPATIBILITY_WARNING);
        }
      }
    },
    [testPermissions.video],
  );

  // Audio analysis functions
  const setupAudioAnalysis = useCallback((mediaStream: MediaStream) => {
    try {
      // Check if the stream has audio tracks
      if (mediaStream.getAudioTracks().length === 0) {
        console.warn('MediaStream has no audio tracks, skipping audio analysis');
        return;
      }

      // Clean up existing audio context
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }

      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(mediaStream);

      analyser.fftSize = 256;
      microphone.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      updateAudioLevel();
    } catch (err) {
      console.error('Failed to setup audio analysis:', err);
    }
  }, []);

  const updateAudioLevel = useCallback(() => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteTimeDomainData(dataArray);

    // Calculate RMS (root mean square) to estimate loudness more accurately
    const rms = Math.sqrt(
      dataArray.reduce((sum, val) => {
        const norm = (val - 128) / 128; // normalize from 0-255 to -1 to 1
        return sum + norm * norm;
      }, 0) / dataArray.length,
    );

    // Apply noise gate - ignore values below threshold
    const threshold = 0.03;
    setAudioLevel(rms > threshold ? rms : 0);

    // Check if we have detected audio
    if (rms > threshold) {
      hasAudioTestDetected.current = true;
    }

    animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
  }, []);

  // Enhanced camera switching with loading states
  const handleSwitchCamera = useCallback(() => {
    if (!canSwitchCamera(testPermissions.video) || isSwitchingCamera) return;

    setIsSwitchingCamera(true);
    setIsStreamLoading(true);
    setError(null);

    try {
      // Simple toggle - Webcam handles the stream management
      setIsFrontCamera((prev) => !prev);

      // Add delay for visual feedback and stream stabilization
      setTimeout(() => {
        setIsSwitchingCamera(false);
        setIsStreamLoading(false);
      }, 1000);
    } catch (err) {
      console.error('Failed to switch camera:', err);
      setError('Failed to switch camera. This device may not have multiple cameras.');
      setIsSwitchingCamera(false);
      setIsStreamLoading(false);
    }
  }, [testPermissions.video, isSwitchingCamera]);

  // Media test initialization
  const startMediaTest = useCallback(
    async (permissions?: { video?: boolean; audio?: boolean }) => {
      const effectivePermissions = permissions || testPermissions;

      try {
        setError(null);
        setHasStartedTest(true);
        setIsStreamLoading(true);

        // Give Android Chrome time to initialize camera drivers
        if (isAndroid()) {
          await new Promise((resolve) => setTimeout(resolve, 800));
        }

        // Check browser compatibility
        checkBrowserCompatibility(effectivePermissions);

        // Request separate audio stream for audio testing (video test uses video-only stream)
        if (effectivePermissions.audio) {
          const audioStream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: false,
          });
          audioStreamRef.current = audioStream;
          setupAudioAnalysis(audioStream);
        }
      } catch (err) {
        console.error('Failed to start media test:', err);
        setBrowserWarning(CAMERA_COMPATIBILITY_WARNING);
        setHasStartedTest(false);
      }
    },
    [testPermissions, checkBrowserCompatibility],
  );

  const stopMediaTest = useCallback(() => {
    // Clean up audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // Stop separate audio stream if it exists
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    setHasStartedTest(false);
    setAudioLevel(0);
    setError(null);
    setBrowserWarning(null);
    setIsSwitchingCamera(false);
    setIsStreamLoading(false);
    hasAudioTestDetected.current = false;
  }, []);

  // Video constraints for Webcam component
  const getVideoConstraints = useCallback(
    () =>
      createVideoConstraints({
        facingMode: isFrontCamera ? FacingMode.USER : FacingMode.ENVIRONMENT,
        quality: RecordingQuality.LOW, // Use low quality for testing to ensure compatibility
      }),
    [isFrontCamera],
  );

  // Webcam callbacks
  const onUserMediaError = useCallback((error: string | DOMException) => {
    console.error('Webcam error:', error);
    // Camera failed - show warning with copy link option (handled by UI)
    setBrowserWarning(CAMERA_COMPATIBILITY_WARNING);
    setIsStreamLoading(false);
    setIsSwitchingCamera(false);
  }, []);

  const onUserMedia = useCallback(() => {
    setIsStreamLoading(false);
    setIsSwitchingCamera(false);
    setError(null);
    setBrowserWarning(null);
  }, []);

  return {
    // State
    showMediaTestModal,
    testPermissions,
    isFrontCamera,
    isStreamLoading,
    isSwitchingCamera,
    hasStartedTest,
    error,
    browserWarning,
    audioLevel,
    hasAudioTestDetected,

    // Computed
    canSwitchCamera: canSwitchCamera(testPermissions.video),
    isVideoReady: !testPermissions.video || (testPermissions.video && !browserWarning),
    isAudioReady:
      !testPermissions.audio ||
      (testPermissions.audio && hasAudioTestDetected.current && !browserWarning),

    // Actions
    openMediaTest,
    openCameraTest,
    openMicrophoneTest,
    openBothMediaTest,
    closeMediaTest,
    handleTestComplete,
    startMediaTest,
    stopMediaTest,
    handleSwitchCamera,
    setupAudioAnalysis,

    // Webcam integration
    getVideoConstraints,
    onUserMediaError,
    onUserMedia,
  };
}
